/** @jsx jsx */
import { jsx, css } from '@emotion/react';
// eslint-disable-next-line no-unused-vars
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { VerticalBarChart } from './vChart';
import { HorizontalBarChart } from './hChart';
import { useOrientedChart } from 'hooks/useOrientedChart';
import { SubtitleCountRenderer } from 'components/virtualTooltip/renderers';
import { useWidgetControlsContext } from 'views/common/WidgetControls';

/**
 * Renders the bar chart, or stacked bar chart.
 *
 * @param {object} data
 * @param {object} dimensions
 * @param {object} widgetConfiguration
 * @param {object} endpointQuery
 * @param {function} getColor
 * @returns {JSX.Element}
 * @constructor
 */
export const StackedBarChartWidget = ({ getColor, data, dimensions, widgetConfiguration, endpointQuery, hideLabels }) => {
	const svgRef = useRef();

	const options = useMemo(() => {
		if (!getColor) return;

		const o = { ...widgetConfiguration, colorScale: getColor };

		// if we have a label set in the widget configuration use that, else if we are aggregating on a property use that
		if (!widgetConfiguration?.yAxisLabel && endpointQuery?.options?.parameters?.aggregationProperty) {
			o.yAxisLabel = endpointQuery.options.parameters.aggregationProperty;
		}

		return o;
	}, [getColor, widgetConfiguration, endpointQuery]);

	const {dispatch:controlsDispatch, state:controlsState} = useWidgetControlsContext()
	// Filter data to match legend controls
	const [filteredData,setFilteredData] = useState(data)
	useEffect(()=>{
		if(controlsState?.legend) {
			let filteredLines = {...data.lines}
			Object.entries(controlsState.legend.keyValues).forEach(([key,isEnabled])=>{
				if(!isEnabled) filteredLines[key] = []
			})

			// Reconstruct data without disabled data
			const newFilteredData = {
				...data,
				data: data.data.map(o => {
					const toReturn = {...o};
					data.keys.forEach(k => {
						if(!controlsState.legend.keyValues[k] && toReturn.hasOwnProperty(k)) {
							toReturn.value -= toReturn[k];
							delete toReturn[k];
						}
					})

					return toReturn;
				}),
				rolled:data.rolled.filter(
					(k)=>controlsState.legend.keyValues[k.key]
				),

			}
			if (newFilteredData.extent[1] <= 0) newFilteredData.extent[1] = 1
			setFilteredData(newFilteredData)
		}
		else {
			if (data.extent[1] <= 0) data.extent[1] = 1
			setFilteredData(data);
		}
	},[controlsState?.legend,data]);


	const { TooltipElement, chartMethods } = useOrientedChart({
		svgRef,
		data: filteredData,
		dimensions,
		options,
		orientation: widgetConfiguration?.direction ?? 'auto',
		hideLabels,
		VerticalChart: VerticalBarChart,
		HorizontalChart: HorizontalBarChart,
		renderer: SubtitleCountRenderer,
	});

	// Keep legend up to date
	useEffect(()=>{
		//This may not be the best way to determine if the chart is stacked or not??
		if(data.keys.length !== data.properties.length && chartMethods) {
			controlsDispatch({
				type: "setLegend",
				payload: {
					keys: data.keys,
					colorScale: getColor,
					//@ts-ignore
					mouseOver: text => chartMethods?.mouseOver(null,{key:text}),
					//@ts-ignore
					mouseOut: text => chartMethods?.mouseOut(null,{key:text}),
				}
			})
		}
	},[data.rolled,getColor,controlsDispatch, chartMethods])

	return (
		<div style={{ position: 'relative' }}>
			{TooltipElement}
			<svg cy-test="stackbar-data" width={dimensions.width} height={dimensions.height} ref={svgRef} css={chartStyle} />
		</div>
	);
};

const chartStyle = css`
	.gridGroup {
		line,
		path {
			stroke: lightgrey;
			stroke-dasharray: 2;
		}
	}
`;
